// App-level error boundary. Catches render-time crashes per route so a
// single broken component doesn't white-screen the whole CRM.

import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCcw, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface to console for debugging — in prod we'd report to a remote logger.
    console.error("[CRM ErrorBoundary]", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex h-full w-full items-center justify-center bg-surface px-6 py-12">
        <div className="max-w-lg space-y-4 rounded-lg border border-danger/30 bg-danger/5 p-6 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/15 text-danger">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h2 className="font-display text-xl font-semibold text-ink">Something broke on this page</h2>
          <p className="text-sm text-ink-muted">
            {this.state.error.message || "An unexpected error occurred."}
          </p>
          <details className="rounded border border-line bg-surface-panel2 p-2 text-left text-xs text-ink-dim">
            <summary className="cursor-pointer">Stack trace</summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[10px] scrollbar-thin">
              {this.state.error.stack}
            </pre>
          </details>
          <div className="flex justify-center gap-2">
            <Button size="sm" variant="outline" onClick={this.reset} className="gap-1.5">
              <RefreshCcw className="h-3.5 w-3.5" /> Try again
            </Button>
            <Button size="sm" className="gap-1.5" asChild>
              <a href="/">
                <HomeIcon className="h-3.5 w-3.5" /> Back to home
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
